import moment from "moment-timezone";
import { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import DurationInput, { getDurationInputValue } from "../../../../../components/Inputs/DurationInput";
import { parseFloat2, renderDurationAsFormat } from "../../../../../utils/helpers/date";
import { isAddedTimeSheet } from "../helpers";
import { useAccess } from "../../../../../utils/hooks/access";
import NumberInput from "../../../../../components/Inputs/NumberInput";
import { usePayCodesMap } from "../../../../Settings/PayPolicies/CalculationGroup/Form/hooks/usePayCodeMap";
import { getMandatoryBreak, isMandatoryBreakViolated } from "../../MandatoryBreakWarning/helpers";

export const getTimeSheetDuration = (timeSheet) => {
    const { startDate, endDate, payCode } = timeSheet;
    if (!startDate || !endDate) {
        return 0;
    }
    if (payCode?.hourType === "unit") {
        return timeSheet.duration;
    }
    return moment(endDate).diff(moment(startDate), "second");
}

export const useDurationColumn = ({ canEdit, openMandatoryBreakWarning }) => {
    const { t } = useTranslation();
    const durationInputRef = useRef();
    const numberInputRef = useRef();
    const { hasAccess: canUpdateDuration } = useAccess("timeSheet.canUpdateDuration");

    const map = usePayCodesMap();

    return useMemo(() => ({
        Header: t("duration"),
        accessor: "duration",
        alwaysVisible: true,
        Cell: ({ row, cell, updateAddedTimeSheet, updateTimeSheet }) => {
            const { original, isNonProductive } = row;
            const { startDate, endDate, locked } = original;
            const payCode = map?.[original?.payCode?.code]
            if (!startDate) {
                return null;
            }

            const disabled = (!isAddedTimeSheet(original) && locked)
                || row.disabled || cell.disabled;

            if (isNonProductive) {
                return "-";
            }

            if (payCode?.hourType === "unit" || (canUpdateDuration && payCode?.hourType !== "amount")) {
                if (payCode?.inputDurationAsNumber) {
                    return (
                        <NumberInput
                            ref={numberInputRef}
                            value={parseFloat2(moment.duration(original.duration, "second").asHours() || 0)}
                            disabled={disabled || !canEdit}
                            className="time-input"
                            style={{
                                color: '#595959',
                                backgroundColor: 'transparent',
                                border: '1px solid #d9d9d9',
                                fontSize: '0.9rem',
                            }}
                            onChange={(e) => {
                                let val = Number(String(e.target.value).replace(/,/g, ""));
                                if (isNaN(val)) {
                                    return;
                                }
                                const currentValue = parseFloat2(moment.duration(original.duration, "second").asHours() || 0);
                                if (val === currentValue) {
                                    return;
                                }
                                if (val < 0) {
                                    val = 0;
                                }
                                let duration = (startDate && endDate)
                                    ? moment.parseZone(endDate).diff(moment.parseZone(startDate), "seconds")
                                    : 0;
                                let endD = endDate;

                                if (val) {
                                    duration = moment.duration(val, "hour").asSeconds();
                                }

                                if (duration > 0) {
                                    endD = moment.parseZone(startDate).add(duration, "seconds").toISOString(true);
                                }

                                const updatedFields = { duration, endDate: endD };

                                const changeValue = (data) => {
                                    if (isAddedTimeSheet(original)) {
                                        updateAddedTimeSheet(original, data);
                                    } else {
                                        updateTimeSheet(original.id, data);
                                    }
                                }

                                const mandatoryBreak = openMandatoryBreakWarning ? getMandatoryBreak(original) : null;
                                if (mandatoryBreak) {
                                    const inViolation = isMandatoryBreakViolated(
                                        {
                                            ...original,
                                            ...updatedFields,
                                            payCode: original.payCode || updatedFields.payCode,
                                        },
                                        mandatoryBreak,
                                    );
                                    if (inViolation) {
                                        openMandatoryBreakWarning({
                                            column: "duration",
                                            requiredTime: mandatoryBreak.duration,
                                            original,
                                            updatedFields,
                                            onConfirm: () => {
                                                changeValue(updatedFields);
                                            },
                                            onCancel: () => {
                                                // WORKAROUND FOR NUMBER INPUT
                                                numberInputRef.current?.reset();
                                            },
                                        });
                                    } else {
                                        changeValue(updatedFields);
                                    }
                                } else {
                                    changeValue(updatedFields);
                                }
                            }}
                        />
                    );
                }
                const value = renderDurationAsFormat(original.duration, "HH:mm")
                return (
                    <DurationInput
                        ref={durationInputRef}
                        value={value}
                        disabled={disabled || !canEdit}
                        timeFormat="HH:mm"
                        className="time-input"
                        style={{
                            color: '#595959',
                            backgroundColor: 'transparent',
                            border: '1px solid #d9d9d9',
                            fontSize: '0.9rem',
                        }}
                        onChange={(val) => {
                            let duration = (startDate && endDate)
                                ? moment.parseZone(endDate).diff(moment.parseZone(startDate), "seconds")
                                : 0;
                            let endD = endDate;

                            if (val) {
                                duration = getDurationInputValue(val);
                            }

                            if (duration > 0) {
                                endD = moment.parseZone(startDate).add(duration, "seconds").toISOString(true);
                            }

                            const updatedFields = { duration, endDate: endD };

                            const changeValue = (data) => {
                                if (isAddedTimeSheet(original)) {
                                    updateAddedTimeSheet(original, data);
                                } else {
                                    updateTimeSheet(original.id, data);
                                }
                            }

                            const mandatoryBreak = openMandatoryBreakWarning ? getMandatoryBreak(original) : null;
                            if (mandatoryBreak) {
                                const inViolation = isMandatoryBreakViolated(
                                    {
                                        ...original,
                                        ...updatedFields,
                                        payCode: original.payCode || updatedFields.payCode,
                                    },
                                    mandatoryBreak,
                                );
                                if (inViolation) {
                                    openMandatoryBreakWarning({
                                        column: "duration",
                                        requiredTime: mandatoryBreak.duration,
                                        original,
                                        updatedFields,
                                        onConfirm: () => changeValue(updatedFields),
                                        onCancel: () => durationInputRef.current?.reset(),
                                    });
                                } else {
                                    changeValue(updatedFields)
                                }
                            } else {
                                changeValue(updatedFields);
                            }
                        }}
                    />
                );
            } else if (payCode?.hourType === "amount") {
                return "-"
            } else {
                const duration = (endDate && startDate) ? moment(endDate).diff(moment(startDate), "second") : 0;
                return renderDurationAsFormat(duration, "HH:mm");
            }
        },
    }), [t, map, canUpdateDuration, canEdit, openMandatoryBreakWarning]);
};
